import { Home, Package, Settings, Users } from 'lucide-react';
import type { NavigationItem } from '@klareospace/pharos-lib-web-utils';
import type { MasterContextValue } from '&/master';
import type { UseUserScopesReturn } from '&/hooks/use-user-scopes';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BuildSettingsNavItemsParams = Pick<
  UseUserScopesReturn,
  | 'canAccessSites'
  | 'canAccessSpaces'
  | 'canAccessTenantSettings'
  | 'hasMultipleSites'
  | 'hasMultipleSpaces'
  | 'preferSitesTab'
  | 'singleSiteId'
  | 'singleSpaceId'
>;

export type BuildMasterContextSettingsNavItemsParams = Pick<MasterContextValue, 'siteId' | 'spaceId'> &
  BuildSettingsNavItemsParams;

// ─── Static items ─────────────────────────────────────────────────────────────

/**
 * Sidebar main navigation items for the frontoffice.
 *
 * Each entry maps to a top-level authenticated route.
 * The order here determines the display order in the sidebar.
 */
export const SIDEBAR_NAV_ITEMS: NavigationItem[] = [
  { icon: Home, labelKey: 'navigation.home', path: '/' },
  { icon: Package, labelKey: 'navigation.orders', path: '/orders', requiredPermission: 'orders:list' },
  { icon: Users, labelKey: 'navigation.customers', path: '/customers', requiredPermission: 'customers:list' },
];

// ─── Dynamic builders ─────────────────────────────────────────────────────────

/**
 * Builds the bottom sidebar navigation items using MasterContext as the
 * primary source of truth for the Settings destination URL.
 *
 * Priority:
 * 1. MasterContext has siteId  → `/settings/sites/$siteId`
 * 2. MasterContext has spaceId → `/settings/spaces/$spaceId`
 * 3. MasterContext at tenant level (both null) → delegate to `buildSettingsNavItems`
 *
 * The Settings entry is only shown when the user has at least one of
 * `tenants:read`, `spaces:list`, or `sites:list`.
 */
export function buildMasterContextSettingsNavItems(params: BuildMasterContextSettingsNavItemsParams): NavigationItem[] {
  const { canAccessSites, canAccessSpaces, canAccessTenantSettings, preferSitesTab, siteId, spaceId, ...staticParams } =
    params;
  const { hasMultipleSpaces } = staticParams;

  const hasAnySettingsPermission = canAccessTenantSettings || canAccessSpaces || canAccessSites;
  if (!hasAnySettingsPermission) return [];

  // MasterContext has a site selected → link directly to that site's settings
  if (siteId) {
    return [{ icon: Settings, labelKey: 'navigation.settings', path: `/settings/sites/${siteId}` }];
  }

  // MasterContext has a space selected → link directly to that space's settings
  // Users with sites:list but no spaces:list should land on the sites tab (the general tab requires spaces:list)
  if (spaceId) {
    const subPath = preferSitesTab ? `/settings/spaces/${spaceId}/sites` : `/settings/spaces/${spaceId}`;
    return [{ icon: Settings, labelKey: 'navigation.settings', path: subPath }];
  }

  // Tenant level — fall back to static cascade
  const staticItems = buildSettingsNavItems({
    canAccessSites,
    canAccessSpaces,
    canAccessTenantSettings,
    preferSitesTab,
    ...staticParams,
  });
  if (staticItems.length > 0) return staticItems;

  // Cross-space-sites fallback: the static cascade couldn't build a URL (no singleSpaceId)
  // because this user's site scopes span multiple parent spaces (hasMultipleSpaces=true from
  // resolvedScopes). Before MasterContext initializes, point them to /settings so the sidebar
  // item appears immediately. This only activates for users who span multiple spaces but have
  // no spaces:list permission and no singleSpaceId to build a scoped URL from.
  if (canAccessSites && hasMultipleSpaces) {
    return [{ icon: Settings, labelKey: 'navigation.settings', path: '/settings' }];
  }

  return [];
}

/**
 * Builds the bottom sidebar navigation items dynamically based on the user's
 * permissions and scope flags (whether the user has access to multiple spaces/sites).
 *
 * Returns **at most one** item — always labelled "Settings" with a `Settings` icon.
 * Only the destination URL varies based on the user's highest-privilege scope:
 *
 * 1. `tenants:read`                                      → `/settings`
 * 2. `spaces:list` + hasMultipleSpaces                   → `/settings/spaces`
 * 3. `spaces:list` + !hasMultipleSpaces + singleSpaceId  → `/settings/spaces/:id`
 * 4. `sites:list`  + hasMultipleSites                    → `/settings/spaces/:id/sites`
 * 5. `sites:list`  + !hasMultipleSites + singleSiteId    → `/settings/sites/:id`
 * 6. (none of the above)                                 → empty array
 *
 * Each level is exclusive: once a match is found the cascade stops immediately.
 */
export function buildSettingsNavItems(params: BuildSettingsNavItemsParams): NavigationItem[] {
  const {
    canAccessSites,
    canAccessSpaces,
    canAccessTenantSettings,
    hasMultipleSites,
    hasMultipleSpaces,
    singleSiteId,
    singleSpaceId,
  } = params;

  // 1. Tenant admin — highest priority
  if (canAccessTenantSettings) {
    return [{ icon: Settings, labelKey: 'navigation.settings', path: '/settings' }];
  }

  // 2 & 3. Space admin — only when tenant settings are absent
  const spaceItem = resolveSpaceItem({ canAccessSpaces, hasMultipleSpaces, singleSpaceId });
  if (spaceItem) return [spaceItem];

  // 4 & 5. Site admin — only when no spaces entry matched
  const siteItem = resolveSiteItem({ canAccessSites, hasMultipleSites, singleSiteId, singleSpaceId });
  if (siteItem) return [siteItem];

  // 6. No matching permission/count combination
  return [];
}

// ─── Private resolvers ────────────────────────────────────────────────────────

interface ResolveSpaceItemParams {
  canAccessSpaces: boolean;
  hasMultipleSpaces: boolean;
  singleSpaceId: string | null;
}

function resolveSpaceItem({
  canAccessSpaces,
  hasMultipleSpaces,
  singleSpaceId,
}: ResolveSpaceItemParams): NavigationItem | null {
  if (!canAccessSpaces) return null;

  if (hasMultipleSpaces) {
    return { icon: Settings, labelKey: 'navigation.settings', path: '/settings/spaces' };
  }

  if (singleSpaceId) {
    return { icon: Settings, labelKey: 'navigation.settings', path: `/settings/spaces/${singleSpaceId}` };
  }

  return null;
}

interface ResolveSiteItemParams {
  canAccessSites: boolean;
  hasMultipleSites: boolean;
  singleSiteId: string | null;
  singleSpaceId: string | null;
}

function resolveSiteItem({
  canAccessSites,
  hasMultipleSites,
  singleSiteId,
  singleSpaceId,
}: ResolveSiteItemParams): NavigationItem | null {
  if (!canAccessSites) return null;

  if (hasMultipleSites) {
    // Multi-site users need a spaceId to build the sites list URL.
    // singleSpaceId is now always available for site-scoped users via useUserScopes.
    if (!singleSpaceId) return null;
    return { icon: Settings, labelKey: 'navigation.settings', path: `/settings/spaces/${singleSpaceId}/sites` };
  }

  if (singleSiteId) {
    return { icon: Settings, labelKey: 'navigation.settings', path: `/settings/sites/${singleSiteId}` };
  }

  return null;
}
